import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatIcon from "@mui/icons-material/Chat";
import GroupIcon from "@mui/icons-material/Group";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { CreateGroupDialog } from "./CreateGroupDialog.tsx";
import pusher from "../../tools/pusher";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../api_Routes";
import { useNavigate } from "react-router-dom";
import {StartNewConversationDialog} from "./StartNewConversationDialog.tsx";

import React, { useEffect, useState } from "react";
import { ChatListWrapper } from "./ChatListWrapper";
import { CreateChannelDialog } from "./CreateChannelDialog.tsx";

interface ChatListProps {
  conversations: any[];
  setReciverName: any;
  reciverName: string;
  setReciverImage: any;
  reciverImage: string;
  groups: any[];
  channels: any[];
  setSelectedConversation: (id: number) => void;
  selectedConversation: number | null;
  setChatType: (type: "individual" | "group" | "channel") => void;
  chatType: "individual" | "group" | "channel";
  isLoading: boolean;
  onMarkAsRead?: (id: number, type: "individual" | "group" | "channel") => void;
  setConversations?: React.Dispatch<React.SetStateAction<any[]>>;
  setGroups?: React.Dispatch<React.SetStateAction<any[]>>;
  setChannels?: React.Dispatch<React.SetStateAction<any[]>>;
}

export const ChatList = ({
  conversations,
  reciverName,
  setReciverName,
  reciverImage,
  setReciverImage,
  groups,
  channels,
  setSelectedConversation,
  selectedConversation,
  setChatType,
  chatType,
  isLoading,
  onMarkAsRead,
  setConversations,
  setGroups,

  setChannels,
}: ChatListProps) => {
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [startNewConversation, setStartNewConversation] = useState(false);

  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  // Get current user ID from localStorage
  const getCurrentUserId = () => {
    const userString = localStorage.getItem("user");
    if (userString) {
      const user = JSON.parse(userString);
      return user.user_id;
    }
    return null;
  };

  // Function to fetch conversation details
  const fetchConversationDetails = async (conversationId: number) => {
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.UserChat.View}?conversation_id=${conversationId}`,
        hasToken: true,
      });
      return response?.data;
    } catch (error) {
      // console.error("Error fetching conversation details:", error);
      return null;
    }
  };

  // Function to fetch group details
  const fetchGroupDetails = async (groupId: number) => {
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.UserChat.GroupInfo}?group_id=${groupId}`,
        hasToken: true,
      });
      return response?.data;
    } catch (error) {
      // console.error("Error fetching group details:", error);
      return null;
    }
  };

  // Function to fetch group details
  const fetchChannelDetails = async (channelId: number) => {
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.UserChat.ChannelInfo}?channel_id=${channelId}`,
        hasToken: true,
      });
      return response?.data;
    } catch (error) {
      // console.error("Error fetching group details:", error);
      return null;
    }
  };

  const handleConversationSelect = (
    reciverId,
    conversationId: number,
    type: "individual" | "group" | "channel",
    user_type,
    name,
    image
  ) => {
    setChatType(type);
    setReciverName(name)
    setReciverImage(image)
    setSelectedConversation(conversationId);
    if (user_type === 'App\\Models\\User'){
      navigate(`/chats/${type}/user/${reciverId}`);
    }else if (user_type === 'App\\Models\\Admin'){
      navigate(`/chats/${type}/admin/${reciverId}`);
    }
    else{
      navigate(`/chats/${type}/${reciverId}`);
    }
    if (onMarkAsRead) {
      onMarkAsRead(conversationId, type);
    }
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: "individual" | "group" | "channel"
  ) => {
    setChatType(newValue);
    setSelectedConversation(null)
    navigate(`/chats/${newValue}`);
  };

  const handleGroupCreated = () => {
    // Refresh the groups list
    window.location.reload();
  };

  // Listen to real-time message updates
  useEffect(() => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId || !setConversations || !setGroups || !setChannels)
      return;

    const channelName = `private-newmessage${currentUserId}`;
    // console.log(`Subscribing to chat list updates: ${channelName}`);

    const channel = pusher.subscribe(channelName);

    channel.bind("pusher:subscription_succeeded", () => {
      // console.log("✅ Successfully subscribed to chat list updates");
    });

    channel.bind("pusher:subscription_error", (error: any) => {
      // console.error("❌ Chat list subscription error:", error);
    });

    const handleNewMessage = (data: any) => {
      // console.log("Received new message for chat list update:", data);

      // Validate the data
      if (!data || !data.type || !data.chat_id || !data.body) {
        // console.warn("Invalid message data received:", data);
        return;
      }

      if (data.type === "chat" && setConversations) {
        // Update individual conversations
        setConversations((prevConversations) => {
          const updatedConversations = [...prevConversations];

          // Find the conversation that received the message
          const conversationIndex = updatedConversations.findIndex(
            (conv) => conv.id === data.chat_id
          );

          if (conversationIndex !== -1) {
            // Update the conversation with new message and increment unread count
            const updatedConversation = {
              ...updatedConversations[conversationIndex],
              last_message: {
                body: data.body,
                created_at: new Date().toISOString(),
              },
              unread_count:
                (updatedConversations[conversationIndex].unread_count || 0) + 1,
            };

            // Remove the conversation from its current position
            updatedConversations.splice(conversationIndex, 1);

            // Add it to the top of the list
            updatedConversations.unshift(updatedConversation);

            // Fetch actual conversation details in the background
            fetchConversationDetails(data.chat_id)
              .then((details) => {
                if (details && setConversations) {
                  setConversations((prevConversations) => {
                    const updatedConversations = [...prevConversations];
                    const index = updatedConversations.findIndex(
                      (conv) => conv.id === data.chat_id
                    );
                    if (index !== -1) {
                      updatedConversations[index] = {
                        ...updatedConversations[index],
                        ...details,
                      };
                    }
                    return updatedConversations;
                  });
                }
              })
              .catch((error) => {
                // console.error("Error fetching conversation details:", error);
              });
          } else {
            // If conversation doesn't exist, create a new one and add it to the top
            // This handles the case where a new conversation is started
            const newConversation = {
              id: data.chat_id,
              name: data.sender_name || "Unknown User",
              last_message: {
                body: data.body,
                message: data.body,
                created_at: new Date().toISOString(),
              },
              unread_count: 1,
              avatar: undefined, // You might want to fetch this from the backend
            };

            updatedConversations.unshift(newConversation);

            // Fetch actual conversation details in the background
            fetchConversationDetails(data.chat_id)
              .then((details) => {
                if (details && setConversations) {
                  setConversations((prevConversations) => {
                    const updatedConversations = [...prevConversations];
                    const index = updatedConversations.findIndex(
                      (conv) => conv.id === data.chat_id
                    );
                    if (index !== -1) {
                      updatedConversations[index] = {
                        ...updatedConversations[index],
                        ...details,
                      };
                    }
                    return updatedConversations;
                  });
                }
              })
              .catch((error) => {
                // console.error("Error fetching conversation details:", error);
              });
          }

          return updatedConversations;
        });
      } else if (data.type === "group" && setGroups) {
        // Update group conversations
        setGroups((prevGroups) => {
          const updatedGroups = [...prevGroups];

          // Find the group that received the message
          const groupIndex = updatedGroups.findIndex(
            (group) => group.id === data.chat_id
          );

          if (groupIndex !== -1) {
            // Update the group with new message and increment unread count
            const updatedGroup = {
              ...updatedGroups[groupIndex],
              last_message: {
                message: data.body,
                created_at: new Date().toISOString(),
              },
              unread_count: (updatedGroups[groupIndex].unread_count || 0) + 1,
            };

            // Remove the group from its current position
            updatedGroups.splice(groupIndex, 1);

            // Add it to the top of the list
            updatedGroups.unshift(updatedGroup);

            // Fetch actual group details in the background
            fetchGroupDetails(data.chat_id)
              .then((details) => {
                if (details && setGroups) {
                  setGroups((prevGroups) => {
                    const updatedGroups = [...prevGroups];
                    const index = updatedGroups.findIndex(
                      (group) => group.id === data.chat_id
                    );
                    if (index !== -1) {
                      updatedGroups[index] = {
                        ...updatedGroups[index],
                        ...details,
                      };
                    }
                    return updatedGroups;
                  });
                }
              })
              .catch((error) => {
                // console.error("Error fetching group details:", error);
              });
          } else {
            // If group doesn't exist, create a new one and add it to the top
            // This handles the case where a user is added to a new group
            const newGroup = {
              id: data.chat_id,
              name: `Group ${data.chat_id}`, // You might want to fetch the actual group name
              last_message: {
                message: data.body,
                created_at: new Date().toISOString(),
              },
              unread_count: 1,
            };

            updatedGroups.unshift(newGroup);

            // Fetch actual group details in the background
            fetchGroupDetails(data.chat_id)
              .then((details) => {
                if (details && setGroups) {
                  setGroups((prevGroups) => {
                    const updatedGroups = [...prevGroups];
                    const index = updatedGroups.findIndex(
                      (group) => group.id === data.chat_id
                    );
                    if (index !== -1) {
                      updatedGroups[index] = {
                        ...updatedGroups[index],
                        ...details,
                      };
                    }
                    return updatedGroups;
                  });
                }
              })
              .catch((error) => {
                // console.error("Error fetching group details:", error);
              });
          }

          return updatedGroups;
        });
      } else if (data.type === "channel" && setChannels) {
        // Update group conversations
        setChannels((prevChannels) => {
          const updatedChannels = [...prevChannels];

          // Find the group that received the message
          const channelIndex = updatedChannels.findIndex(
            (channel) => channel.id === data.chat_id
          );

          if (channelIndex !== -1) {
            // Update the group with new message and increment unread count
            const updatedChannel = {
              ...updatedChannels[channelIndex],
              last_message: {
                message: data.body,
                created_at: new Date().toISOString(),
              },
              unread_count:
                (updatedChannels[channelIndex].unread_count || 0) + 1,
            };

            // Remove the group from its current position
            updatedChannels.splice(channelIndex, 1);

            // Add it to the top of the list
            updatedChannels.unshift(updatedChannel);

            // Fetch actual channel details in the background
            fetchChannelDetails(data.chat_id)
              .then((details) => {
                if (details && setChannels) {
                  setChannels((prevChannels) => {
                    const updatedChannels = [...prevChannels];
                    const index = updatedChannels.findIndex(
                      (channel) => channel.id === data.chat_id
                    );
                    if (index !== -1) {
                      updatedChannels[index] = {
                        ...updatedChannels[index],
                        ...details,
                      };
                    }
                    return updatedChannels;
                  });
                }
              })
              .catch((error) => {
                // console.error("Error fetching group details:", error);
              });
          } else {
            // If group doesn't exist, create a new one and add it to the top
            // This handles the case where a user is added to a new group
            const newChannel = {
              id: data.chat_id,
              name: `Channel ${data.chat_id}`, // You might want to fetch the actual group name
              last_message: {
                message: data.body,
                created_at: new Date().toISOString(),
              },
              unread_count: 1,
            };

            updatedChannels.unshift(newChannel);

            // Fetch actual channel details in the background
            fetchChannelDetails(data.chat_id)
              .then((details) => {
                if (details && setChannels) {
                  setChannels((prevChannels) => {
                    const updatedChannels = [...prevChannels];
                    const index = updatedChannels.findIndex(
                      (channel) => channel.id === data.chat_id
                    );
                    if (index !== -1) {
                      updatedChannels[index] = {
                        ...updatedChannels[index],
                        ...details,
                      };
                    }
                    return updatedChannels;
                  });
                }
              })
              .catch((error) => {
                // console.error("Error fetching group details:", error);
              });
          }

          return updatedChannels;
        });
      }
    };

    channel.bind("NewMessageSent", handleNewMessage);

    return () => {
      // console.log("Cleaning up chat list subscription");
      channel.unbind_all();
      pusher.unsubscribe(channelName);
    };
  }, [setConversations, setGroups, setChannels]);

  const renderChatItem = (
    item: any,
    type: "individual" | "group" | "channel"
  ) => {
   return <ListItem
        component="div"
        sx={{
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          "&:hover": {
            backgroundColor: selectedConversation === item.id ? '#3f78e0' : "rgba(0, 0, 0, 0.04)",
          },
          height: '100px',
          cursor: "pointer",
          justifyContent: 'space-between',
          borderRadius: '5px',
          backgroundColor:
              selectedConversation === item.id ? '#3f78e0' : "inherit",
        }}
        onClick={() => {
          type === "individual"
              ? getCurrentUserId() === item.advertiser_id
              ? handleConversationSelect(item.cutomer_id, item.id, type, item.customer_type, item.name, item.image)
              : handleConversationSelect(item.advertiser_id, item.id, type, item.advertiser_type, item.name, item.image)
              : handleConversationSelect(item.id, item.id, type, null, item.name, item.image);
        }}
    >
      <ListItemAvatar>
        <Badge
            color="error"
            variant="dot"
            invisible={!item.unread_count}
            sx={{
              '& .MuiBadge-badge': {
                height: 14,
                minWidth: 14,
                borderRadius: '50%',
                top: 8,    // Adjust these values
                right: 8,  // to position the badge
              }
            }}
        >
          <Avatar
              sx={{
                width: 60,
                height: 60,
                fontSize: '1.5rem',  // For text content
                '& .MuiSvgIcon-root': {  // For GroupIcon
                  fontSize: '2rem'
                }
              }}
              src={(type === "group" || type === "channel") ? item.avatar : (item.image ? item.image : undefined)}
          >
            {type === "group" ? <GroupIcon/> : item.name?.[0]}
          </Avatar>
        </Badge>
      </ListItemAvatar>
      <Box
          sx={{width: '80%'}}
      >
        <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
        >
          <Typography
              variant="body2"

              sx={{
                maxWidth: "200px",
                fontSize: "20px",
                color: selectedConversation === item.id ? '#ffffff' : '#000000'
              }}
          >
            {item.name}
          </Typography>
          <Typography variant="caption" color={selectedConversation === item.id ? '#ffffff' : "text.secondary"}>
            {item.last_message?.created_at
                ? new Date(item.last_message.created_at).toLocaleTimeString()
                : ""}
          </Typography>
        </Box>
        <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
        >
          <Box sx={{
            display: "flex",
            flexDirection: "row",
            width: '100%',
            justifyContent: 'space-between'
          }}>
            <Typography
                variant="body2"
                color={selectedConversation === item.id ? '#ffffff' : "text.secondary"}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                  fontSize: "18px",
                }}

            >
              {(type !== "individual"
                  ? item.last_message?.message
                  : item.last_message?.body) || "No messages yet"}
            </Typography>

          </Box>
          {item.unread_count > 0 && (
              <Chip
                  label={item.unread_count}
                  color="primary"
                  size="small"
                  sx={{ml: 1}}
              />
          )}
        </Box>
      </Box>
      {/*<ListItemText*/}
      {/*  primary={item.name}*/}
      {/*  */}
      {/*/>*/}
    </ListItem>
  }

  return (
    <Box
    sx={{
      width: isMobile ? '100%' :'25%',
      borderRight: "1px solid rgba(0, 0, 0, 0.12)",
      height: "100%",
      overflowY: "hidden",
    }}
    >
      <Box
        p={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Chats</Typography>
        {chatType === "group" && (
          <IconButton
            color="primary"
            onClick={() => setCreateGroupOpen(true)}
            sx={{ ml: 1 }}
          >
            <AddIcon />
          </IconButton>

        )}
        {chatType === "channel" && (
          <IconButton
            color="primary"
            onClick={() => setCreateChannelOpen(true)}
            sx={{ ml: 1 }}
          >
            <AddIcon />
          </IconButton>
        )}
        {chatType === "individual" && (
            <IconButton
                color="primary"
                onClick={() => setStartNewConversation(true)}
                sx={{ ml: 1 }}
            >
                <AddIcon />
            </IconButton>
        )}
      </Box>
      <Divider />
      {isMobile && selectedConversation ? (
        <Box sx={{ p: 1, display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => navigate(`/chats/${chatType}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="subtitle1">Back to chats</Typography>
        </Box>
      ) : (
        <></>
      )}
      <Tabs
        value={chatType}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          value="individual"
          label="Individual"
          icon={<ChatIcon />}
          iconPosition="start"
        />
        <Tab
          value="group"
          label="Groups"
          icon={<GroupIcon />}
          iconPosition="start"
        />
        <Tab
          value="channel"
          label="Channels"
          icon={<GroupIcon />}
          iconPosition="start"
        />
      </Tabs>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List sx={{ overflow: "auto", height: "calc(100vh - 180px)" ,padding:'5px'}}>
          {chatType === "individual" ? (
            conversations.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No conversations available
                </Typography>
              </Box>
            ) : (
              conversations.map((conversation) =>
                renderChatItem(conversation, "individual")
              )
            )
          ) : chatType === "group" ? (
            groups.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No groups available
                </Typography>
              </Box>
            ) : (
              groups.map((group) => renderChatItem(group, "group"))
            )
          ) : channels.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No Channels available
              </Typography>
            </Box>
          ) : (
            channels.map((channel) => renderChatItem(channel, "channel"))
          )}
        </List>
      )}

      <CreateGroupDialog
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        onGroupCreated={handleGroupCreated}
      />
        <StartNewConversationDialog
            open={startNewConversation}
            setSelectedConversation={setSelectedConversation}
            onClose={() => setStartNewConversation(false)}
            onGroupCreated={handleGroupCreated}
        />
      <CreateChannelDialog
        open={createChannelOpen}
        onClose={() => setCreateChannelOpen(false)}
        onGroupCreated={handleGroupCreated}
      />
    </Box>


  );
};
